import { useThemeContext } from "@circle-react/providers/Theme";
import { Modal } from "@circleco/compass/components/Modal";
import type { Editor } from "@tiptap/core";
import { useRef } from "react";

import { t } from "@/i18n-js/instance";

import { ButtonForm } from "./ButtonForm";

interface CTASettingsModalComponentProps {
  editor: Editor;
  isWithinEmailEditor?: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  range: { from: number; to: number };
}

const FORM_ID = "tiptap-cta-settings-form";

export const CTASettingsModalComponent = ({
  editor,
  range,
  isWithinEmailEditor = false,
  open,
  onOpenChange,
}: CTASettingsModalComponentProps) => {
  const theme = useThemeContext();
  const inputRef = useRef<HTMLInputElement>(null);

  if (!editor) return null;

  const defaultBackgroundColor = theme.brandColor["light"] ?? "#000000";
  const defaultTextColor = theme.brandTextColor["light"] ?? "#FFFFFF";

  const getDefaultValues = () => {
    const {
      label = "",
      url = "",
      color = defaultBackgroundColor,
      text_color = defaultTextColor,
      may_lead_to_purchase = false,
      ...rest
    } = editor.isActive("cta") ? editor.getAttributes("cta") : {};
    return {
      label,
      url,
      color,
      text_color,
      may_lead_to_purchase,
      ...rest,
    } as const;
  };

  const defaultValues = getDefaultValues();
  const isEdit = !!defaultValues.label;

  const handleClose = () => onOpenChange(false);

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange} size="md">
      <Modal.Content>
        <Modal.Header
          title={
            isEdit
              ? t("email_editor.cta.edit_title")
              : t("email_editor.cta.title")
          }
        />
        <Modal.Body>
          <ButtonForm
            formId={FORM_ID}
            hideActions
            inputRef={inputRef}
            range={range}
            editor={editor}
            onClose={handleClose}
            defaultValues={defaultValues}
            isWithinEmailEditor={isWithinEmailEditor}
          />
        </Modal.Body>
        <Modal.Footer
          primaryAction={{
            type: "submit",
            form: FORM_ID,
            label: t("email_editor.cta.save"),
          }}
          secondaryAction={{
            label: t("cancel"),
            onClick: handleClose,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
